'use client';

import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { QuestionMarkCircleIcon } from 'assets/icons';
import { CustomImage } from 'components/shared/custom-image';
import { maxAsset } from 'content/products/max/assets';
import { FSAHSAComponent } from 'components/shared/HsaFsaComponent';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';
import { useMaxPlanCopy } from 'components/shared/Cart/hooks/useMaxPlanCopy';
import { useChipRowFit } from 'components/shared/Buy/hooks/useChipRowFit';
import { getMaxValueSummary } from 'content/products/max/pricing';
import { getMaxRowsForRegion } from 'content/products/max/library';
import {
  MaxPlanKey,
  MaxSelection,
  MaxStoreConfig,
  MAX_VISIBLE_CHIP_FALLBACK,
  MaxFunnelChipKey,
  getMaxCycleLabelKey,
  getMaxFunnelChips,
  isMaxUsCopy,
} from 'content/products/max';
import styles from './MaxSelectionContent.module.css';

interface MaxSelectionContentProps {
  config: MaxStoreConfig;
  currencySymbol: ReactNode;
  value: MaxSelection;
  onChange: (value: MaxSelection) => void;
  /** Buttons stay disabled until the ring variant resolves, like the other steps. */
  disabled: boolean;
  /** GTM `data-product`, e.g. "Ultrahuman Ring AIR". */
  productName: string;
  onLearnMore: () => void;
  onCoverageInfo?: () => void;
  /** Extra trade-in credit MAX unlocks, in store currency. 0 hides the banner. */
  tradeInBonus?: number;
}

/** Figma's one check glyph (2569:113891, 2569:113944); colour comes from the caller. */
const MaxCheck = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
    <path
      d="M3 7.3L5.6 9.8L11 4.2"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FUNNEL_PLAN_ORDER: MaxPlanKey[] = ['monthly', 'yearly'];
const COVERAGE_KEYS = ['a', 'b'] as const;
/** Collapsed chip row: Figma 2832:103219 shows two rows; gap matches `.chips`. */
const CHIP_ROWS = 2;
const CHIP_GAP = 4;

/**
 * Ultrahuman MAX step body shared by the Ring AIR and Ring PRO funnels.
 * Rendered inside each funnel's `Section` (title, muted subtitle, info icon).
 */
export const MaxSelectionContent = ({
  config,
  currencySymbol,
  value,
  onChange,
  disabled,
  productName,
  onLearnMore,
  onCoverageInfo,
  tradeInBonus = 0,
}: MaxSelectionContentProps) => {
  const t = useTranslations('ring');
  const formatPrice = useFormatPrice();
  const { region, currency, store } = useRegionLocale();
  const { isCnOPlus } = useIsCnOPlus();
  const isUsCopy = isMaxUsCopy(region);
  const chips = useMemo(() => getMaxFunnelChips(region), [region]);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const chipsRef = useRef<HTMLDivElement>(null);
  const chipsMeasureRef = useRef<HTMLDivElement>(null);
  const fitCount = useChipRowFit({
    containerRef: chipsRef,
    measureRef: chipsMeasureRef,
    maxRows: CHIP_ROWS,
    gap: CHIP_GAP,
    fallback: MAX_VISIBLE_CHIP_FALLBACK,
  });
  const visibleChips = chipsExpanded ? chips : chips.slice(0, fitCount);
  const moreCount = chipsExpanded ? 0 : chips.length - fitCount;
  const cycleLabelKey = getMaxCycleLabelKey(isCnOPlus);
  const {
    label,
    perMonthEquivalentLabel,
    yearlySaving,
    showHsaBadge,
    entryPrice,
  } = useMaxPlanCopy();
  // The funnel renders `currencySymbol` as a node; the badge and price row need it as text.
  const symbolText = getCurrencySymbolFromEnum(currency);
  const saveBadge = yearlySaving(config, symbolText);
  const entry = entryPrice(config, symbolText);
  // Same "bought separately" figure the explainer modal shows (Figma 2660:12230).
  const savePercent = useMemo(
    () =>
      getMaxValueSummary(getMaxRowsForRegion(region), store, isCnOPlus)
        .savePercent,
    [region, store, isCnOPlus],
  );
  const yearlyPerMonth = perMonthEquivalentLabel(config, 'yearly');
  const openCoverage = onCoverageInfo ?? onLearnMore;

  const chipLabel = (key: MaxFunnelChipKey): string => {
    if (isUsCopy) return t(`ring.productSelection.maxSection.us.chips.${key}`);
    if (key === 'cycle') {
      return t(`ring.productSelection.maxSection.chips.${cycleLabelKey}`);
    }
    return t(`ring.productSelection.maxSection.chips.${key}`);
  };
  const moreLabel = (count: number): string =>
    t('ring.productSelection.maxSection.chips.more', { count });
  const renderChip = (key: MaxFunnelChipKey) => (
    <div key={key} className={styles.chip}>
      <span className={styles.chipIcon}>
        <MaxCheck className={styles.chipCheck} />
      </span>
      <span className={styles.chipLabel}>{chipLabel(key)}</span>
    </div>
  );

  return (
    <div className={`${styles.root} max-content`}>
      <div className={styles.hero}>
        <div className={styles.priceRow}>
          <p className={styles.priceLabel}>{entry.text}</p>
          {savePercent ? (
            <span className={styles.savingsPill}>
              {t('ring.productSelection.maxSection.savingsPill', {
                percent: savePercent,
              })}
            </span>
          ) : null}
        </div>
        <div className={styles.orbs}>
          <CustomImage
            className={styles.orbsImg}
            src={maxAsset('hero-orbs-3.png')}
            alt={t('ring.productSelection.maxSection.heroAlt')}
            width={329}
            height={100}
          />
        </div>
        <div className={styles.body}>
          <div className={styles.intro}>
            <p className={styles.description}>
              {isUsCopy
                ? t('ring.productSelection.maxSection.us.heroBody')
                : t('ring.productSelection.maxSection.heroBody')}
            </p>
            <div ref={chipsRef} className={styles.chips}>
              {visibleChips.map(renderChip)}
              {moreCount > 0 ? (
                <button
                  type="button"
                  className={`${styles.chip} ${styles.chipMore} max-chip-more`}
                  data-testid="ring-max-chips-more"
                  onClick={() => setChipsExpanded(true)}
                >
                  <span className={styles.chipLabel}>
                    {moreLabel(moreCount)}
                  </span>
                </button>
              ) : null}
              <div
                ref={chipsMeasureRef}
                className={styles.chipsMeasure}
                aria-hidden
                inert
              >
                {chips.map(renderChip)}
                <div className={styles.chip}>
                  <span className={styles.chipLabel}>
                    {moreLabel(Math.max(moreCount, 1))}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className={`${styles.learnMore} max-learn-more`}
              onClick={onLearnMore}
            >
              {isUsCopy
                ? t('ring.productSelection.maxSection.us.learnMore')
                : t('ring.productSelection.maxSection.learnMore')}
              {/* Inline, in the link's colour, so a host page's accent
                  (Ring PRO's CTA blue) reaches the chevron too. Same path
                  as the old uh-max/chevron.svg. */}
              <svg
                className={styles.learnMoreChevron}
                viewBox="0 0 10 10"
                width={10}
                height={10}
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M3.5 2L6.5 5L3.5 8"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className={styles.coverage}>
            <div className={styles.coverageBody}>
              <div className={styles.coverageHeader}>
                <p className={styles.coverageTitle}>
                  {t('ring.productSelection.maxSection.coverage.title')}
                </p>
                <button
                  type="button"
                  className={`${styles.coverageInfo} max-coverage-info`}
                  onClick={openCoverage}
                  aria-label={t(
                    'ring.productSelection.maxSection.coverage.title',
                  )}
                >
                  <QuestionMarkCircleIcon />
                </button>
              </div>
              <ul className={styles.coverageList}>
                {COVERAGE_KEYS.map((key) => (
                  <li key={key} className={styles.coverageItem}>
                    <MaxCheck className={styles.check} />
                    <span>
                      {t(`ring.productSelection.maxSection.coverage.${key}`)}
                    </span>
                  </li>
                ))}
                {tradeInBonus > 0 ? (
                  <li className={styles.coverageItem}>
                    <MaxCheck className={styles.check} />
                    <span>
                      {t('ring.productSelection.maxSection.coverage.tradeIn', {
                        amount: `${symbolText}${formatPrice(tradeInBonus)}`,
                      })}
                    </span>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.plans}>
        {FUNNEL_PLAN_ORDER.map((plan) => {
          const isYearly = plan === 'yearly';
          return (
            <button
              key={plan}
              type="button"
              className={`${styles.plan} gtm ${value === plan ? 'active' : ''}`}
              data-buttontype={`max selection ${plan}`}
              data-product={productName}
              data-testid={`ring-max-${plan}`}
              aria-pressed={value === plan}
              onClick={() => onChange(plan)}
              disabled={disabled}
            >
              <div className={styles.planRow}>
                <div className={styles.planName}>
                  <strong>{label(config, plan)}</strong>
                  {showHsaBadge(config, plan) ? (
                    <FSAHSAComponent className={styles.hsa} />
                  ) : null}
                </div>
                <div className={styles.price}>
                  {currencySymbol}
                  {formatPrice(config.plans[plan].price)}
                </div>
              </div>
              {isYearly && (saveBadge || yearlyPerMonth) ? (
                <div className={styles.planMeta}>
                  {saveBadge ? (
                    <span className={styles.saveTag}>{saveBadge}</span>
                  ) : (
                    <span />
                  )}
                  {yearlyPerMonth ? (
                    <span className={styles.thatsJust}>
                      ({t('ring.productSelection.maxSection.thatsJust')}{' '}
                      {currencySymbol}
                      {yearlyPerMonth})
                    </span>
                  ) : null}
                </div>
              ) : null}
            </button>
          );
        })}
        <button
          type="button"
          className={`${styles.decline} no-max gtm ${
            value === false ? 'active' : ''
          }`}
          data-buttontype="max selection"
          data-product={productName}
          data-default-option="true"
          data-crawler-recommended="true"
          data-testid="ring-max-decline"
          aria-pressed={value === false}
          onClick={() => onChange(false)}
          disabled={disabled}
        >
          <strong>{t('ring.productSelection.noMax')}</strong>
        </button>
      </div>
    </div>
  );
};
